import { useRestQuery, useRestMutation } from '../tanstack-query/restHooks'
import { queryKeys } from '../tanstack-query/queryClient'
import { queryClient } from '../tanstack-query/queryClient'
import { RechargeType, QuizAnswers, GameColleague, GameResult, LeaderboardEntry, UserGameStats } from '@vacationtracker/shared/types/recharge-type'

// Response types
export interface GameColleaguesResponse {
  colleagues: GameColleague[]
  companyRechargeTypeCount: number
}

export interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[]
  userStats: UserGameStats | null
  userRank: number | null
}

export interface PTOLeaveTypeNamesResponse {
  ptoLeaveTypeNames: string[]
}

/**
 * Hook to save user's recharge type (quiz results)
 *
 * @example
 * const { mutate: saveRechargeType, isLoading } = useSaveUserRechargeType({
 *   onSuccess: () => {
 *     // Invalidate user recharge type query
 *     queryClient.invalidateQueries(queryKeys.rechargeType.userRechargeType())
 *   }
 * })
 *
 * saveRechargeType({ rechargeType: 'SOCIAL_SPRINTER', quizAnswers: {...} })
 */
export function useSaveUserRechargeType(options?: { onSuccess?: () => void; onError?: (error: Error) => void }) {
  return useRestMutation<void, { rechargeType: RechargeType; quizAnswers: QuizAnswers }>('/core/save-recharge-type', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rechargeType.userRechargeType() })
      options?.onSuccess?.()
    },
    onError: options?.onError,
  })
}

/**
 * Hook to fetch game colleagues
 *
 * @param limit - Number of colleagues to fetch (default: 10)
 * @param options - Additional query options
 *
 * @example
 * const { data, isLoading } = useGameColleagues(10)
 *
 * if (data) {
 *   const colleagues = data.colleagues
 *   const count = data.companyRechargeTypeCount
 * }
 */
export function useGameColleagues(
  limit = 10,
  options?: {
    enabled?: boolean
  }
) {
  return useRestQuery<GameColleaguesResponse>(queryKeys.rechargeType.gameColleagues(limit), `/core/game/colleagues?limit=${limit}`, {
    enabled: options?.enabled !== false,
    staleTime: 2 * 60 * 1000, // 2 minutes - game colleagues can change
  })
}

/**
 * Hook to fetch company recharge type count
 *
 * @example
 * const { data: count } = useCompanyRechargeTypeCount()
 */
export function useCompanyRechargeTypeCount(options?: { enabled?: boolean }) {
  const query = useRestQuery<GameColleaguesResponse>(
    queryKeys.rechargeType.companyCount(),
    '/core/game/colleagues?limit=0', // limit=0 returns only count
    {
      enabled: options?.enabled !== false,
      staleTime: 5 * 60 * 1000, // 5 minutes - count doesn't change frequently
    }
  )

  // Return query with count extracted for convenience
  return {
    ...query,
    data: query.data?.companyRechargeTypeCount,
  }
}

/**
 * Hook to save game result
 *
 * @example
 * const { mutate: saveResult, isLoading } = useSaveGameResult({
 *   onSuccess: () => {
 *     // Invalidate leaderboard and user stats
 *     queryClient.invalidateQueries({ queryKey: queryKeys.rechargeType.leaderboard() })
 *   }
 * })
 *
 * saveResult({ streak: 5 })
 */
export function useSaveGameResult(options?: { onSuccess?: () => void; onError?: (error: Error) => void }) {
  return useRestMutation<void, GameResult>('/core/game/result', {
    onSuccess: () => {
      // Invalidate leaderboard and user stats queries
      // Use prefix match to invalidate all leaderboard queries regardless of limit
      queryClient.invalidateQueries({
        queryKey: ['rechargeType', 'game', 'leaderboard'],
        exact: false, // Match all queries starting with this prefix
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.rechargeType.userRechargeType() })
      options?.onSuccess?.()
    },
    onError: options?.onError,
  })
}

/**
 * Hook to fetch game leaderboard
 *
 * @param limit - Number of leaderboard entries to fetch (default: 10)
 * @param options - Additional query options
 *
 * @example
 * const { data, isLoading } = useGameLeaderboard(10)
 *
 * if (data) {
 *   const leaderboard = data.leaderboard
 *   const userStats = data.userStats
 *   const userRank = data.userRank
 * }
 */
export function useGameLeaderboard(
  limit = 10,
  options?: {
    enabled?: boolean
    refetchOnMount?: boolean
  }
) {
  return useRestQuery<LeaderboardResponse>(queryKeys.rechargeType.leaderboard(limit), `/core/game/leaderboard?limit=${limit}`, {
    enabled: options?.enabled !== false,
    staleTime: 1 * 60 * 1000, // 1 minute - leaderboard can change frequently
    retry: 3, // Retry failed requests
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: options?.refetchOnMount,
  })
}

/**
 * Hook to fetch PTO leave type names
 *
 * @example
 * const { data: ptoNames } = usePTOLeaveTypeNames()
 *
 * if (ptoNames) {
 *   const names = ptoNames.ptoLeaveTypeNames
 * }
 */
export function usePTOLeaveTypeNames(options?: { enabled?: boolean }) {
  const query = useRestQuery<PTOLeaveTypeNamesResponse>(queryKeys.rechargeType.ptoLeaveTypeNames(), '/core/pto-leave-type-names', {
    enabled: options?.enabled !== false,
    staleTime: 30 * 60 * 1000, // 30 minutes - PTO names don't change often
  })

  // Return data with ptoLeaveTypeNames extracted for convenience
  return {
    ...query,
    ptoLeaveTypeNames: query.data?.ptoLeaveTypeNames || [],
  }
}
